import { UserRole, formatMoney, type JobSummary } from '@teentack/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  BadgeCheckIcon,
  MapPinIcon,
  ShieldCheckIcon,
  UsersIcon,
  WalletIcon,
} from '@/components/ui/Icons';
import { TrustBadge } from '@/features/connections/TrustBadge';

/**
 * One live opportunity, with the teen's next action on it.
 *
 * The card never decides policy — the server already told us whether this teen
 * has shortlisted it, applied, and where guardian consent stands. The card just
 * renders the one action that makes sense now. [Teen steps 7-10]
 */
export function JobListingCard({
  job,
  role,
  needsConsent = true,
  onToggleShortlist,
  onRequestConsent,
  onApply,
  onBid,
  busy,
}: {
  job: JobSummary;
  role: string;
  /**
   * Whether this teen is a minor. An adult teen has no consent row, so without
   * this the card reads "no consent yet" and offers to ask a guardian who does
   * not exist. Defaults to the cautious answer.
   */
  needsConsent?: boolean;
  onToggleShortlist: () => void;
  onRequestConsent: () => void;
  onApply: () => void;
  /** Open-budget jobs are bid on, not applied for. */
  onBid?: () => void;
  busy?: boolean;
}) {
  const isTeen = role === UserRole.TEEN;
  const mayEngage = !needsConsent || job.consentStatus === 'APPROVED';
  const slotsLeft = job.teensNeeded - job.teensAssigned;

  /** The single next step, so the teen is never shown two competing buttons. */
  function primaryAction() {
    if (!isTeen) return null;

    if (job.appliedByViewer) {
      return (
        <Badge tone="safe" icon={<BadgeCheckIcon className="size-3.5" />}>
          Applied
        </Badge>
      );
    }

    if (mayEngage) {
      // An open-budget job takes an offer rather than an application, so the
      // card sends the teen to the page where they can name a price.
      if (job.payType === 'BID') {
        return (
          <Button onClick={onBid ?? onApply} disabled={busy}>
            Place a bid
          </Button>
        );
      }

      return (
        <Button onClick={onApply} disabled={busy}>
          Apply now
        </Button>
      );
    }

    if (job.consentStatus === 'PENDING') {
      return (
        <Badge tone="sun" icon={<ShieldCheckIcon className="size-3.5" />}>
          Waiting for guardian
        </Badge>
      );
    }

    if (job.consentStatus === 'DECLINED') {
      return (
        <Button variant="outline" onClick={onRequestConsent} disabled={busy}>
          Ask again
        </Button>
      );
    }

    // A minor with no consent row yet. Adults never reach this — they are
    // handled by `mayEngage` above.
    return (
      <Button onClick={onRequestConsent} disabled={busy}>
        Ask my guardian
      </Button>
    );
  }

  return (
    <article className="flex flex-col gap-4 rounded-2xl border border-line bg-white p-5 shadow-soft transition hover:border-brand-200 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-ink">{job.title}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="font-semibold text-slate-600">{job.posterName}</span>
            {(job.city || job.location) && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="size-3.5 text-slate-400" />
                {job.location ?? [job.city, job.state].filter(Boolean).join(', ')}
              </span>
            )}
          </p>
        </div>

        <button
          type="button"
          onClick={onToggleShortlist}
          disabled={busy}
          aria-pressed={job.savedByViewer}
          aria-label={job.savedByViewer ? 'Remove from shortlist' : 'Save to shortlist'}
          className={`shrink-0 rounded-xl border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
            job.savedByViewer
              ? 'border-brand-600 bg-brand-600 text-white'
              : 'border-line bg-white text-slate-600 hover:border-brand-300 hover:bg-brand-50'
          }`}
        >
          {job.savedByViewer ? 'Saved' : 'Save'}
        </button>
      </div>

      <TrustBadge trust={job.trust} />

      <p className="line-clamp-2 text-sm leading-relaxed text-slate-600">{job.description}</p>

      {job.skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {job.skills.slice(0, 4).map((skill) => (
            <Badge key={skill.publicId} tone="neutral">
              {skill.name}
            </Badge>
          ))}
          {job.skills.length > 4 && <Badge tone="neutral">+{job.skills.length - 4}</Badge>}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5 font-bold text-ink">
            <WalletIcon className="size-4 text-safe-600" />
            {formatMoney(job.pay.amountMinor, job.pay.currency)}
            <span className="text-xs font-medium text-slate-400">
              {job.payType === 'BID' ? 'open to bids' : 'per teen'}
            </span>
          </span>

          {job.needType === 'MULTIPLE_TEENS' && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <UsersIcon className="size-4 text-slate-400" />
              {slotsLeft} of {job.teensNeeded} places left
            </span>
          )}
        </div>

        {primaryAction()}
      </div>
    </article>
  );
}
